import { Heightfield } from '../terrain/heightfield'
import { ObstacleField } from '../physics/obstacles'
import { Skier } from '../physics/skier'
import type { InputFrame } from '../physics/skier'
import { clamp } from '../core/math'
import { quantiseSteer } from './ghost'

/**
 * A reactive autopilot: ski the fall line, dodge what's in the way.
 *
 * This replaces the par recording as the thing the screenshot harness and the
 * audit ride. Par is gone — the game is arcade best-times now — but the jobs
 * par quietly did still need doing: the harness needs a rider that survives
 * long enough to photograph the mountain, and the audit needs a *skiable line*
 * proof plus a real recording for the ghost-determinism checks. A bot that
 * gets down the hill covers all three, and it is a more honest proof than the
 * beam search was: if whisker-steering can thread the forest, a human with
 * eyes definitely can.
 *
 * It is deliberately thin, because the physics already does the hard part: the
 * skier auto-centers toward the fall line (the sin/cos assist in skier.ts) and
 * drainage is enforced on every slope, so an input-less rider follows the
 * gully by construction. The bot adds tuck for speed, steering away from
 * obstacles, and one piece of state — commitment.
 *
 * It steers by *seeking the clearest gap*, not by fleeing the nearest tree.
 * The first build was pure repulsion — push away from whatever a whisker hit —
 * and four rounds of tuning never fixed it, because repulsion has the trap
 * built in: in a dense stand every pocket of trees pushes inward, the pushes
 * cancel, and the bot wiggles in place or ricochets from one pocket into the
 * next. Each knob traded one crash flavour for another (tree impacts for
 * sideways landings for stalls), which is the signature of a controller that
 * is architecturally wrong rather than badly tuned. Scoring candidate
 * headings by clearance-plus-downhill and committing briefly to the winner
 * threads the same forest at the first attempt.
 *
 * Determinism holds the same way it does everywhere in the sim: arithmetic on
 * sim state plus state that chains only from sim state, no atan2 (see the
 * assist comment for why), no randomness, steer quantised exactly as the
 * recorder stores it. A recorded bot run replays float-identically.
 */

/** Candidate headings, radians off the current one. Straight on first. */
const GAPS = [0, -0.22, 0.22, -0.45, 0.45, -0.7, 0.7] as const
/** Sample points along each candidate ray, as fractions of reach. */
// The first sample sits close in. It started at 0.3 of reach, and the trace
// found the bot pinned against a rock on a forty-degree slope, state sketch,
// forever: the rock stood *inside* the first sample, every candidate ray saw
// clean past it, every gap scored clear, and gravity pressed the rider back
// into the rock each tick. A blind zone at your feet is the one place a
// clearance sensor cannot afford one.
const MARCH = [0.12, 0.3, 0.55, 0.8, 1.0] as const
/** How long to hold a chosen gap before re-deciding, ticks. */
const HOLD = 18

export class Autopilot {
  /** Currently held gap direction, radians off heading at choose time. */
  private held = 0
  private holdTicks = 0
  /** Full-lock escape from standing contact, ticks left and direction. */
  private escapeTicks = 0
  private escapeDir = 1
  /** Consecutive escapes without real progress; relaxes the exit condition. */
  private escapeAttempts = 0
  private escapeZ = -Infinity
  /** Ticks spent at walking pace with no crash to explain it. */
  private slowTicks = 0

  reset(): void {
    this.held = 0
    this.holdTicks = 0
    this.escapeTicks = 0
    this.escapeDir = 1
    this.escapeAttempts = 0
    this.escapeZ = -Infinity
    this.slowTicks = 0
  }

  step(skier: Skier, hf: Heightfield, obstacles: ObstacleField, out: InputFrame): void {
    // Never steer in the air: a rider mid-dodge at takeoff arrives sideways.
    if (skier.airborne) {
      out.steer = 0
      out.brake = false
      out.tuck = false
      out.pop = false
      return
    }

    const fx = Math.sin(skier.heading)
    const fz = Math.cos(skier.heading)

    // Standing against something is its own mode. Gap-seeking fails there by
    // construction — the adjacent obstacle sits inside every candidate ray's
    // first sample, all gaps score identically blocked, and the tiebreak picks
    // straight ahead: the tail trace showed steer pinned at 0.00 against a
    // tree for two hundred seconds. At contact, stop scoring and just turn:
    // full lock toward the freer flank, tuck to build speed, hold it two
    // seconds. Reaction resumes once moving.
    if (this.escapeTicks > 0) {
      // Stop pivoting the moment the way ahead opens. A fixed two-second
      // hold spun straight past the gap and back to the tree — the trace
      // showed a heading wound through minus fifteen radians, two and a half
      // full turns on the spot.
      // Exit only onto a heading that is both clear and pointing down-course
      // (+z): a clear heading across the slope walks straight into the next
      // pocket at zero speed, which the trace showed as an escape/re-wedge
      // limit cycle parked on one rock.
      // First attempt insists on a downhill exit. If that keeps failing in
      // the same pocket, any clear heading will do — a human traverses out
      // of a cage of trees; only the direct line is actually caged.
      const wantDownhill = this.escapeAttempts < 2
      // The exit probe is skier-sized, not boulevard-sized. At query radius
      // 1.4 a single 1.7-radius tree demanded over three metres of clearance
      // and blocked the entire downhill cone from inside the escape spin —
      // the bot could never exit toward the one direction that mattered.
      if ((!wantDownhill || Math.cos(skier.heading) > 0.25) &&
          !obstacles.query(skier.x + Math.sin(skier.heading) * 2.5,
                           skier.z + Math.cos(skier.heading) * 2.5, 0.5)) {
        this.escapeTicks = 0
      } else {
        this.escapeTicks--
        out.steer = quantiseSteer(this.escapeDir)
        out.brake = false
        out.tuck = true
        out.pop = false
        return
      }
    }
    // Sustained near-standstill is stuck even with nothing ahead. The Gullet
    // parked the bot on an open 21-degree bench: heading pinned across the
    // slope, sluff drag eating the modest gravity component, speed zero, and
    // no obstacle within two metres to trip the contact trigger — open snow
    // in every direction and not going anywhere. Pivoting toward the fall
    // line is the escape there too; the exit condition already prefers it.
    this.slowTicks = skier.speed < 1 && !skier.airborne && skier.crashTimer <= 0
      ? this.slowTicks + 1 : 0
    if ((skier.speed < 2 && skier.crashTimer <= 0 &&
        obstacles.query(skier.x + fx * 2, skier.z + fz * 2, 1.6)) ||
        this.slowTicks > 90) {
      this.slowTicks = 0
      const left = obstacles.query(skier.x - fz * 5, skier.z + fx * 5, 2.4)
      const right = obstacles.query(skier.x + fz * 5, skier.z - fx * 5, 2.4)
      // Blocked left goes right, blocked right goes left. When neither flank
      // decides it, alternate: a pocket that defeats the rightward spin is
      // often open to the left, and repeating the same choice repeats the
      // same failure.
      this.escapeDir = left && !right ? 1 : right && !left ? -1 : -this.escapeDir
      // Real progress since the last escape resets the relaxation.
      this.escapeAttempts = skier.z > this.escapeZ + 4 ? 1 : this.escapeAttempts + 1
      this.escapeZ = skier.z
      this.escapeTicks = 120
      out.steer = quantiseSteer(this.escapeDir)
      out.brake = false
      out.tuck = true
      out.pop = false
      return
    }
    // Reaction distance scales with the stopping problem.
    const reach = clamp(10 + skier.speed * 1.1, 12, 42)

    // How far each candidate heading runs before it meets something.
    let bestScore = -Infinity
    let bestAngle = 0
    let aheadClear = reach
    for (const a of GAPS) {
      const ca = Math.cos(a)
      const sa = Math.sin(a)
      const wx = fx * ca + fz * sa
      const wz = fz * ca - fx * sa
      let clear = reach
      for (const f of MARCH) {
        const hit = obstacles.query(skier.x + wx * reach * f, skier.z + wz * reach * f, 1.8)
        if (hit) {
          clear = Math.hypot(hit.x - skier.x, hit.z - skier.z)
          break
        }
      }
      if (a === 0) aheadClear = clear
      // Clearance first; straight-and-downhill as the tiebreak. The fall-line
      // assist in the sim already pulls the heading downhill, so "prefer
      // straight ahead" is "prefer the fall line" after a moment of skiing.
      const score = clear / reach - Math.abs(a) * 0.18
      if (score > bestScore) {
        bestScore = score
        bestAngle = a
      }
    }

    // Hold a chosen line long enough to actually pass the tree. Re-deciding
    // every tick flickers between gaps that score within noise of each other,
    // which is the wiggle-in-place failure all over again.
    if (this.holdTicks > 0 && aheadClear > skier.speed * 0.45 + 4) {
      this.holdTicks--
    } else {
      this.held = bestAngle
      this.holdTicks = HOLD
    }

    // Proportional steer toward the held gap. Authority shrinks with speed —
    // at pace a violent edge set is itself the crash.
    const maxSteer = clamp(1.3 - skier.speed / 34, 0.55, 1)
    out.steer = quantiseSteer(clamp(this.held * 2.2, -maxSteer, maxSteer))
    // The crashes no gap choice prevents are hard landings, and every one in
    // the Gullet trace was the same setup: carrying speed over a convex
    // roll-over, flying far, landing on ground flatter than the flight. So
    // read the ground: two probes ahead, and if the far interval falls away
    // much faster than the near one, that is a lip — check speed *before* the
    // flight, which is the part a human does with their eyes.
    const g0 = hf.sampleHeight(skier.x, skier.z)
    const gA = hf.sampleHeight(skier.x + fx * 7, skier.z + fz * 7)
    const gB = hf.sampleHeight(skier.x + fx * 14, skier.z + fz * 14)
    const lip = (gA - gB) - (g0 - gA) > 3.5

    // Brake on warning time, on a lip, and on outright speed.
    out.brake = (aheadClear < skier.speed * 0.55 && skier.speed > 11)
      || (lip && skier.speed > 13)
      || skier.speed > 26
    out.tuck = !out.brake && Math.abs(out.steer) < 0.5
    out.pop = false
  }
}
